var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import ArcGISMap from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Extent from '@arcgis/core/geometry/Extent';
import { WGS84, createMarkerUrl, geographicPoint } from '../../../_shared/components/arcgis-map/constants';
import { MapAttribution } from '../../../_shared/components/arcgis-map/components/map-attribution';
const PIN_SIZE = 34;
// The selected home's pin is dropped: a larger head on a stem whose tip marks the house.
const DROPPED_HEAD = 44;
const DROPPED_STEM = 20;
// Symbol sizes are in points; the map measures the screen in pixels.
const POINTS_TO_PIXELS = 4 / 3;
const DROPPED_HEIGHT_PX = (DROPPED_HEAD + DROPPED_STEM) * POINTS_TO_PIXELS;
// How close to an edge a pin may sit and still count as in view.
const EDGE_MARGIN = 28;
// Close enough to a lone home, or to the visitor, to read the streets around it.
const STREET_ZOOM = 17;
// The first view leaves this much room around the outermost homes, so no pin sits on an edge.
const FIT_MARGIN = 1.5;
// The locate button's lowest place, above the map's attribution.
const CLEAR_OF_ATTRIBUTION = 32;
const FALLBACK_COLOR = '#6b2b2b';
const ICON = 'bi bi-house';
const primaryColor = () => {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim();
    return /^#[0-9a-f]{6}$/i.test(value) ? value : FALLBACK_COLOR;
};
const centerOf = (homes) => homes.length === 0
    ? undefined
    : [homes.reduce((sum, h) => sum + h.lng, 0) / homes.length, homes.reduce((sum, h) => sum + h.lat, 0) / homes.length];
const boundsOf = (homes) => {
    const lngs = homes.map(h => h.lng);
    const lats = homes.map(h => h.lat);
    return new Extent({
        'xmin': Math.min(...lngs), 'xmax': Math.max(...lngs),
        'ymin': Math.min(...lats), 'ymax': Math.max(...lats),
        'spatialReference': WGS84(),
    }).expand(FIT_MARGIN);
};
const loadImage = (url) => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
});
const droppedPinUrls = new Map();
// The marker's head with a stem beneath it, drawn at twice the size it shows for sharp screens.
const droppedPinUrl = (color) => {
    const cached = droppedPinUrls.get(color);
    if (cached !== undefined)
        return cached;
    const drawn = createMarkerUrl(color, ICON, 64).then(loadImage).then(head => {
        const scale = 2;
        const canvas = document.createElement('canvas');
        canvas.width = DROPPED_HEAD * scale;
        canvas.height = (DROPPED_HEAD + DROPPED_STEM) * scale;
        const context = canvas.getContext('2d');
        if (context === null)
            return head.src;
        context.fillStyle = color;
        context.fillRect(canvas.width / 2 - 1.5 * scale, DROPPED_HEAD * scale / 2, 3 * scale, (DROPPED_HEAD / 2 + DROPPED_STEM) * scale);
        context.drawImage(head, 0, 0, DROPPED_HEAD * scale, DROPPED_HEAD * scale);
        return canvas.toDataURL('image/png');
    });
    droppedPinUrls.set(color, drawn);
    return drawn;
};
const pinGraphic = (home, visited, selected, color) => __awaiter(void 0, void 0, void 0, function* () {
    return new Graphic({
        'geometry': geographicPoint(home.lng, home.lat),
        'attributes': { 'homeId': home.id },
        // A home already visited draws hollow, so what is left to see stands out.
        'symbol': selected
            ? {
                'type': 'picture-marker',
                'url': yield droppedPinUrl(color),
                'width': DROPPED_HEAD,
                'height': DROPPED_HEAD + DROPPED_STEM,
                // The stem's tip, not the middle of the image, sits on the house.
                'yoffset': (DROPPED_HEAD + DROPPED_STEM) / 2,
            }
            : {
                'type': 'picture-marker',
                'url': yield createMarkerUrl(color, ICON, 64, visited),
                'width': PIN_SIZE,
                'height': PIN_SIZE,
            },
    });
});
const locationGraphic = (location) => new Graphic({
    'geometry': geographicPoint(location.lng, location.lat),
    'symbol': {
        'type': 'simple-marker',
        'style': 'circle',
        'color': [37, 99, 235, 1],
        'size': 14,
        'outline': { 'color': [255, 255, 255, 1], 'width': 3 },
    },
});
// Moves the map, if it must, so the home sits in the open part of it above the card, centred there.
const keepInView = (view, home, obscuredBottom) => {
    const width = view.width;
    const height = view.height;
    const point = view.toScreen(geographicPoint(home.lng, home.lat));
    if (point === null || point === undefined)
        return;
    const openBottom = height - obscuredBottom - EDGE_MARGIN;
    const visible = point.x >= EDGE_MARGIN && point.x <= width - EDGE_MARGIN &&
        point.y >= EDGE_MARGIN + DROPPED_HEIGHT_PX && point.y <= openBottom;
    if (visible)
        return;
    // The pin stands above its point, so the point goes a little below the middle of the open part.
    const desiredY = Math.max(DROPPED_HEIGHT_PX + EDGE_MARGIN, (height - obscuredBottom + DROPPED_HEIGHT_PX) / 2);
    // Shifting the centre by the pin's distance from where it should be: across to the middle, up or down
    // to the middle of the open part.
    const center = view.toMap({ 'x': point.x, 'y': height / 2 + (point.y - desiredY) });
    if (center === null || center === undefined)
        return;
    void view.goTo({ center }, { 'duration': 400 }).catch(() => undefined);
};
// Every home on the tour pinned on one map. Tapping a pin selects its home; the page shows its card.
const TourMap = (props) => {
    const containerRef = useRef(null);
    const pinsRef = useRef(null);
    const locationRef = useRef(null);
    const onSelectRef = useRef(props.onSelect);
    const [view, setView] = useState(null);
    const [locating, setLocating] = useState(false);
    onSelectRef.current = props.onSelect;
    // The homes the map opens on are the ones it fits; it is built once and kept.
    const initialHomes = useRef(props.homes);
    useEffect(() => {
        const container = containerRef.current;
        if (container === null)
            return undefined;
        const pins = new GraphicsLayer();
        const locationLayer = new GraphicsLayer();
        pinsRef.current = pins;
        locationRef.current = locationLayer;
        const mapView = new MapView({
            'map': new ArcGISMap({ 'basemap': 'streets', 'layers': [locationLayer, pins] }),
            'container': container,
            'center': centerOf(initialHomes.current),
            'zoom': 15,
            'constraints': { 'rotationEnabled': false },
            // Pinch and scroll zoom; the design keeps the map clear of buttons.
            'ui': { 'components': [] },
            'attributionVisible': false,
            'popupEnabled': false,
        });
        const click = mapView.on('click', event => {
            void mapView.hitTest(event, { 'include': [pins] }).then(result => {
                var _a;
                const hit = result.results.find(r => r.type === 'graphic');
                const id = (hit === null || hit === void 0 ? void 0 : hit.type) === 'graphic' ? (_a = hit.graphic.attributes) === null || _a === void 0 ? void 0 : _a.homeId : undefined;
                if (typeof id === 'string')
                    onSelectRef.current(id);
            });
        });
        void mapView.when(() => {
            const homes = initialHomes.current;
            const fit = homes.length === 1
                ? mapView.goTo({ 'center': [homes[0].lng, homes[0].lat], 'zoom': STREET_ZOOM }, { 'animate': false })
                : homes.length > 1
                    ? mapView.goTo(boundsOf(homes), { 'animate': false })
                        .then(() => { if (mapView.zoom > STREET_ZOOM + 1)
                        mapView.zoom = STREET_ZOOM + 1; })
                    : Promise.resolve();
            void fit.catch(() => undefined).then(() => setView(mapView));
        });
        return () => {
            click.remove();
            mapView.destroy();
            pinsRef.current = null;
            locationRef.current = null;
            setView(null);
        };
    }, []);
    // The selected home's pin is drawn larger, and last, so nothing covers it.
    useEffect(() => {
        var _a, _b;
        const pins = pinsRef.current;
        if (view === null || pins === null)
            return undefined;
        let cancelled = false;
        const color = primaryColor();
        const selectedId = (_b = (_a = props.selected) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
        const ordered = [
            ...props.shown.filter(h => h.id !== selectedId),
            ...props.shown.filter(h => h.id === selectedId),
        ];
        void Promise.all(ordered.map(h => pinGraphic(h, props.visited.includes(h.id), h.id === selectedId, color)))
            .then(graphics => {
            if (cancelled)
                return;
            pins.removeAll();
            pins.addMany(graphics);
        });
        return () => { cancelled = true; };
    }, [view, props.shown, props.visited, props.selected]);
    useEffect(() => {
        if (view === null || props.selected === null)
            return;
        keepInView(view, props.selected, props.obscuredBottom);
    }, [view, props.selected, props.obscuredBottom]);
    useEffect(() => {
        const layer = locationRef.current;
        if (view === null || layer === null)
            return;
        layer.removeAll();
        if (props.location !== null)
            layer.add(locationGraphic(props.location));
    }, [view, props.location]);
    // Asking for the location does not wait on the map; centring on it does.
    const handleRecenter = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        setLocating(true);
        const location = (_a = props.location) !== null && _a !== void 0 ? _a : yield props.onRequestLocation();
        setLocating(false);
        if (location === null || view === null)
            return;
        void view.goTo({ 'center': [location.lng, location.lat], 'zoom': Math.max(view.zoom, STREET_ZOOM) }, { 'duration': 600 })
            .catch(() => undefined);
    });
    // The button is there while the visitor could still allow their location, or has.
    const canLocate = props.locationAccess === 'granted' || props.locationAccess === 'prompt';
    return (_jsxs("div", { className: "position-absolute top-0 start-0 w-100 h-100", role: "region", "aria-label": "Map of the homes. Every home is also listed on this page.", children: [_jsx("div", { ref: containerRef, className: "tour-map-canvas w-100 h-100" }), _jsx(MapAttribution, { mapView: view }), canLocate &&
                _jsx("button", { type: "button", className: "tour-icon-button tour-map-locate", style: { 'bottom': Math.max(props.obscuredBottom, CLEAR_OF_ATTRIBUTION) }, onClick: handleRecenter, disabled: locating, "aria-label": "Center the map on my location", title: "Center the map on my location", children: _jsx("i", { className: `bi ${locating ? 'bi-hourglass-split' : 'bi-crosshair'}`, "aria-hidden": "true" }) })] }));
};
export default TourMap;
